import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeSelector } from './ThemeToggle';
import LanguageSelector from './LanguageSelector';
import TemplateEditor from './TemplateEditor';
import TemplateManagerSection from './TemplateManagerSection';
import TemplateModal from './TemplateModal';
import LayoutDesignerPro from './layoutDesigner/LayoutDesignerPro';
import AdminConfiguration from './AdminConfiguration';
import { toast } from 'react-toastify';
import api from '../services/api';
import { applyTheme, readStoredTheme } from '../utils/theme';
import './SettingsPage.css';
import { APP_VERSION } from '../version';
import { ChangePasswordToggle } from './ChangePasswordForm';

// SettingsPage - no theme hooks used
const SettingsPage = () => {
  const { t } = useTranslation();
  
  // O tema vem de uma chave só (`theme`), a mesma do cabeçalho e do ThemeProvider (utils/theme.js).
  const [isDarkMode, setIsDarkMode] = useState(readStoredTheme);
  
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    sms: false,
    bookingReminders: true,
    promotions: false,
    weeklyDigest: true
  });
  const [loading, setLoading] = useState(true);
  const [layouts, setLayouts] = useState([]);
  const [documentDefaults, setDocumentDefaults] = useState([]);
  const [documentSeries, setDocumentSeries] = useState([]);
  const [documentSellers, setDocumentSellers] = useState([]);
  const [showDocDefaultsModal, setShowDocDefaultsModal] = useState(false);
  const [docDefaultsForm, setDocDefaultsForm] = useState({ documentType: 'FT', defaultLayoutId: '', defaultSeriesId: '', defaultSellerId: '' });
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [seriesForm, setSeriesForm] = useState({ id: null, documentType: 'FT', name: '', code: '', isDefault: false });
  const [showSellerModal, setShowSellerModal] = useState(false);
  const [sellerForm, setSellerForm] = useState({ id: null, name: '', code: '', isDefault: false });
  const [showLayoutDesigner, setShowLayoutDesigner] = useState(false);
  const [showLayoutDesignerPro, setShowLayoutDesignerPro] = useState(false);
  const [layoutForm, setLayoutForm] = useState(null);
  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const logoInputRef = React.useRef(null);
  const faviconInputRef = React.useRef(null);
  const [activeTab, setActiveTab] = useState('geral');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  // Templates state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [documentLayouts, setDocumentLayouts] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateType, setTemplateType] = useState('email'); // 'email', 'sms', 'layout'

  useEffect(() => {
    loadSettings();
    
    // Check URL params (tab + callbacks)
    const params = new URLSearchParams(window.location.search);

    const tab = (params.get('tab') || '').toLowerCase();
    if (tab) {
      const normalized = tab === 'integracoes' ? 'integrations' : tab;
      const allowed = new Set(['geral', 'email', 'sms', 'documentos', 'integrations']);
      if (allowed.has(normalized)) {
        setActiveTab(normalized);
      }
    }

    if (params.get('calendar') === 'connected') {
      toast.success(t('calendar.synced', 'Google Calendar conectado!'));
      setCalendarConnected(true);
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('calendar') === 'error') {
      toast.error(t('errors.generic', 'Erro ao conectar calendário'));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [t]);

  const loadSettings = async () => {
    try {
      const [calendarStatus, notifPrefs, layoutsRes, compRes, defaultsRes, seriesRes, sellersRes] = await Promise.all([
        api.get('/calendar/status').catch(() => ({ data: { isConnected: false } })),
        api.get('/notifications/preferences').catch(() => ({ data: notifications })),
        api.get('/billing/layouts').catch(() => ({ data: [] })),
        api.get('/gymsettings/company').catch(() => ({ data: null })),
        api.get('/billing/document-defaults').catch(() => ({ data: [] })),
        api.get('/billing/series').catch(() => ({ data: [] })),
        api.get('/billing/sellers').catch(() => ({ data: [] })),
      ]);
      
      setCalendarConnected(calendarStatus.data.isConnected);
      if (notifPrefs.data) {
        setNotifications(notifPrefs.data);
      }
      if (layoutsRes.data) {
        setLayouts(Array.isArray(layoutsRes.data) ? layoutsRes.data : []);
      }

      setDocumentDefaults(Array.isArray(defaultsRes.data) ? defaultsRes.data : []);
      setDocumentSeries(Array.isArray(seriesRes.data) ? seriesRes.data : []);
      setDocumentSellers(Array.isArray(sellersRes.data) ? sellersRes.data : []);

      setCompany(compRes.data || {
        companyName: '', tradeName: '', nif: '', address: '', postalCode: '', city: '', country: 'Portugal',
        phone: '', email: '', website: '', logoUrl: '',
        smtpHost: '', smtpPort: 587, smtpUser: '', smtpPassword: '', smtpFromEmail: '', smtpFromName: '',
        smtpEnableSsl: true, smtpEnabled: false,
        smsSenderId: '', smsEnabled: false,
        imageSearchProvider: '', imageSearchApiKey: '',
        emailSubjectNotification: '', emailTemplateNotification: '',
        emailSubjectDocument: '', emailTemplateDocument: '',
        emailSubjectBooking: '', emailTemplateBooking: '',
        emailSubjectPlan: '', emailTemplatePlan: '',
        emailSubjectCredit: '', emailTemplateCredit: '',
        smsTemplateNotification: '', smsTemplateDocument: '', smsTemplateBooking: '', smsTemplatePlan: '', smsTemplateCredit: '',
        smsParamSendDocuments: false, smsParamSendNotifications: false, smsParamSendBooking: false, smsParamBookingAdvanceMinutes: 30
      });
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await api.get('/calendar/connect');
      window.location.href = response.data.authUrl;
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const disconnectGoogleCalendar = async () => {
    try {
      await api.post('/calendar/disconnect');
      setCalendarConnected(false);
      toast.success('Calendário desconectado');
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const syncCalendar = async () => {
    try {
      await api.post('/calendar/sync');
      toast.success(t('calendar.synced', 'Reservas sincronizadas!'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleNotificationChange = async (key, value) => {
    const newSettings = { ...notifications, [key]: value };
    setNotifications(newSettings);
    
    try {
      await api.put('/notifications/preferences', newSettings);
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    }
  };

  const requestPushPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        toast.success(t('notifications.push', 'Notificações push ativadas!'));
        handleNotificationChange('push', true);
      } else {
        toast.error('Permissão negada');
        handleNotificationChange('push', false);
      }
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const handleSaveCompany = async () => {
    setSaving(true);
    try {
      await api.put('/gymsettings/company', company);
      toast.success('Configurações guardadas!');
    } catch { toast.error('Erro ao guardar'); }
    finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { toast.error('Insira um email para teste'); return; }
    setTesting(true);
    try {
      await api.put('/gymsettings/company', company);
      const res = await api.post('/gymsettings/company/test-email', { email: testEmail });
      toast.success(res.data?.message || 'Email de teste enviado!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao enviar email de teste');
    }
    setTesting(false);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecione uma imagem');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB
      toast.error('Imagem muito grande. Máximo 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/upload/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const url = res.data.url || res.data.path;
      setCompany({ ...company, logoUrl: url });
      toast.success('Logo carregado com sucesso!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Erro ao fazer upload do logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecione uma imagem');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) { // 2MB
      toast.error('Imagem muito grande. Máximo 2MB');
      return;
    }

    setUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/upload/favicon', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const url = res.data.url || res.data.path;
      setCompany({ ...company, faviconUrl: url });
      toast.success('Favicon carregado com sucesso!');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Erro ao fazer upload do favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  // ============================================
  // TEMPLATES MANAGEMENT
  // ============================================

  const loadTemplates = async () => {
    try {
      const [emailRes, smsRes, layoutsRes] = await Promise.all([
        api.get('/templates/email'),
        api.get('/templates/sms'),
        api.get('/templates/layouts')
      ]);
      setEmailTemplates(emailRes.data || []);
      setSmsTemplates(smsRes.data || []);
      setDocumentLayouts(layoutsRes.data || []);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Erro ao carregar templates');
    }
  };

  useEffect(() => {
    if (activeTab === 'email' || activeTab === 'sms' || activeTab === 'documentos') {
      loadTemplates();
    }
  }, [activeTab]);

  const openTemplateModal = (type, template = null) => {
    setTemplateType(type);
    if (template) {
      setEditingTemplate({ ...template });
    } else {
      // New template
      if (type === 'email') {
        setEditingTemplate({
          name: '',
          type: 'Notificacao',
          subject: '',
          body: '',
          tags: '',
          isDefault: false,
          isActive: true
        });
      } else if (type === 'sms') {
        setEditingTemplate({
          name: '',
          type: 'Notificacao',
          body: '',
          tags: '',
          isDefault: false,
          isActive: true
        });
      } else if (type === 'layout') {
        setEditingTemplate({
          name: '',
          documentType: 'FT',
          paperSize: 'A4',
          primaryColor: '#3498db',
          fontSize: 12,
          isDefault: false,
          isActive: true
        });
      }
    }
    setShowTemplateModal(true);
  };

  const closeTemplateModal = () => {
    setShowTemplateModal(false);
    setEditingTemplate(null);
  };

  const handleSaveTemplate = async () => {
    try {
      if (!editingTemplate.name) {
        toast.error('Nome é obrigatório');
        return;
      }

      const endpoint = templateType === 'email' ? '/templates/email' :
                      templateType === 'sms' ? '/templates/sms' :
                      '/templates/layouts';

      if (editingTemplate.id) {
        await api.put(`${endpoint}/${editingTemplate.id}`, editingTemplate);
        toast.success('Template atualizado!');
      } else {
        await api.post(endpoint, editingTemplate);
        toast.success('Template criado!');
      }

      closeTemplateModal();
      loadTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Erro ao guardar template');
    }
  };

  const handleDeleteTemplate = async (type, id) => {
    if (!window.confirm('Tem a certeza que deseja desativar este template?')) return;

    try {
      const endpoint = type === 'email' ? '/templates/email' :
                      type === 'sms' ? '/templates/sms' :
                      '/templates/layouts';

      await api.delete(`${endpoint}/${id}`);
      toast.success('Template desativado!');
      loadTemplates();
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Erro ao desativar template');
    }
  };

  const DOC_TYPES = [
    { value: 'FT', label: 'Fatura' },
    { value: 'FR', label: 'Fatura Recibo' },
    { value: 'FS', label: 'Fatura Simplificada' },
    { value: 'NC', label: 'Nota de Crédito' },
    { value: 'ND', label: 'Nota de Débito' },
    { value: 'OR', label: 'Encomenda Cliente' },
    { value: 'ORC', label: 'Orçamento' },
    { value: 'PP', label: 'Fatura Pro-Forma' },
    { value: 'GT', label: 'Guia de Transporte' },
    { value: 'RC', label: 'Recibo' },
    { value: 'FC', label: 'Fatura de Compra' },
    { value: 'GR', label: 'Guia de Receção (Compras)' },
    { value: 'MS', label: 'Movimentos de Stock' },
    { value: 'INV', label: 'Inventário' },
  ];

  const upsertDocDefaults = async () => {
    try {
      const docType = (docDefaultsForm.documentType || '').trim().toUpperCase();
      if (!docType) { toast.error('Tipo de documento é obrigatório'); return; }

      const payload = {
        defaultLayoutId: docDefaultsForm.defaultLayoutId ? Number(docDefaultsForm.defaultLayoutId) : null,
        defaultSeriesId: docDefaultsForm.defaultSeriesId ? Number(docDefaultsForm.defaultSeriesId) : null,
        defaultSellerId: docDefaultsForm.defaultSellerId ? Number(docDefaultsForm.defaultSellerId) : null,
      };

      await api.put(`/billing/document-defaults/${docType}`, payload);
      toast.success('Valores padrão guardados!');
      setShowDocDefaultsModal(false);
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erro ao guardar valores padrão');
    }
  };

  const saveSeries = async () => {
    try {
      const payload = {
        documentType: (seriesForm.documentType || 'FT').trim().toUpperCase(),
        name: seriesForm.name,
        code: seriesForm.code,
        isDefault: !!seriesForm.isDefault,
      };
      if (!payload.code) { toast.error('Código da série é obrigatório'); return; }

      if (seriesForm.id) await api.put(`/billing/series/${seriesForm.id}`, payload);
      else await api.post('/billing/series', payload);

      toast.success('Série guardada!');
      setShowSeriesModal(false);
      setSeriesForm({ id: null, documentType: 'FT', name: '', code: '', isDefault: false });
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erro ao guardar série');
    }
  };

  const saveSeller = async () => {
    try {
      const payload = { name: sellerForm.name, code: sellerForm.code, isDefault: !!sellerForm.isDefault };
      if (!payload.name) { toast.error('Nome do vendedor é obrigatório'); return; }

      if (sellerForm.id) await api.put(`/billing/sellers/${sellerForm.id}`, payload);
      else await api.post('/billing/sellers', payload);

      toast.success('Vendedor guardado!');
      setShowSellerModal(false);
      setSellerForm({ id: null, name: '', code: '', isDefault: false });
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erro ao guardar vendedor');
    }
  };

  const buildAdvancedLayoutPreviewHtml = (lf) => {
    const pc = lf?.primaryColor || '#2c3e50';
    const ac = lf?.accentColor || '#3498db';
    const ff = lf?.fontFamily || 'Arial, sans-serif';
    const fs = lf?.fontSize || 12;

    const sampleLines = `
      <table style="width:100%;border-collapse:collapse;margin-top:10px">
        <thead>
          <tr style="background:${pc};color:#fff">
            <th style="padding:8px;text-align:left">Ref.</th>
            <th style="padding:8px;text-align:left">Descrição</th>
            <th style="padding:8px;text-align:right">Qtd</th>
            <th style="padding:8px;text-align:right">Preço</th>
            <th style="padding:8px;text-align:right">IVA</th>
            <th style="padding:8px;text-align:right">Total</th>
          </tr>
        </thead>
        <tbody>
          <tr style="border-bottom:1px solid #eee">
            <td style="padding:8px">A1</td>
            <td style="padding:8px">Produto/Serviço Exemplo</td>
            <td style="padding:8px;text-align:right">1,00</td>
            <td style="padding:8px;text-align:right">100,00€</td>
            <td style="padding:8px;text-align:right">23%</td>
            <td style="padding:8px;text-align:right"><strong>100,00€</strong></td>
          </tr>
        </tbody>
      </table>`;

    const tokens = {
      PrimaryColor: pc,
      AccentColor: ac,
      FontFamily: ff,
      FontSize: String(fs),
      CompanyName: 'Nome da Empresa Lda.',
      CompanyNIF: '123456789',
      CompanyAddress: 'Morada, Cidade',
      CompanyPhone: '912345678',
      CompanyEmail: 'empresa@email.pt',
      CompanyLogoUrl: lf?.companyLogoUrl || lf?.logoUrl || '',
      DocumentType: lf?.documentType || 'FT',
      InvoiceNumber: 'FT 2026/0001',
      InvoiceNumberOnly: '0001',
      IssueDate: new Date().toLocaleDateString(),
      DueDate: new Date().toLocaleDateString(),
      ClientName: 'Nome do Cliente',
      ClientNIF: '987654321',
      ClientAddress: 'Morada do Cliente',
      ClientPostalCode: '1000-000',
      ClientEmail: 'cliente@email.pt',
      Subtotal: '100,00€',
      TaxAmount: '23,00€',
      Total: '123,00€',
      PaymentMethod: 'MBWay',
      PaymentTerms: 'Pronto Pagamento',
      Status: 'Emitida',
      Notes: 'Notas do documento...',
      FooterText: 'Documento processado por computador • ' + new Date().toLocaleString(),
      LinesTable: sampleLines,
      LinesRows: ''
    };

    let body = lf?.bodyHtml || '';
    for (const [k, v] of Object.entries(tokens)) {
      body = body.replace(new RegExp(`{{\\s*${k}\\s*}}`, 'gi'), v || '');
    }

    const css = lf?.customCss || '';

    return `
      <style>
        .layout-advanced-preview { font-family: ${ff}; font-size: ${fs}px; color: #333; }
        ${css}
      </style>
      <div class="layout-advanced-preview">${body}</div>
    `;
  };

  if (loading) {
    return (
      <div className="settings-page loading">
        <div className="loader"></div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>{t('settings.title', 'Definições')}</h1>
      </div>

      {/* Tabs Navigation */}
      <div className="settings-tabs">
        <button 
          className={`settings-tab ${activeTab === 'geral' ? 'active' : ''}`}
          onClick={() => setActiveTab('geral')}
        >
          <span className="tab-icon">⚙️</span>
          Geral
        </button>
        <button 
          className={`settings-tab ${activeTab === 'email' ? 'active' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          <span className="tab-icon">📧</span>
          Email
        </button>
        <button 
          className={`settings-tab ${activeTab === 'sms' ? 'active' : ''}`}
          onClick={() => setActiveTab('sms')}
        >
          <span className="tab-icon">📱</span>
          SMS
        </button>
        <button 
          className={`settings-tab ${activeTab === 'documentos' ? 'active' : ''}`}
          onClick={() => setActiveTab('documentos')}
        >
          <span className="tab-icon">📐</span>
          Documentos
        </button>

        <button 
          className={`settings-tab ${activeTab === 'integrations' ? 'active' : ''}`}
          onClick={() => setActiveTab('integrations')}
        >
          <span className="tab-icon">🔌</span>
          Integrações
        </button>
      </div>

      <div className="settings-content">
        {/* Tab: Geral */}
        {activeTab === 'geral' && (
          <>
        {/* Appearance Section */}
        <section className="settings-section">
          <h2>
            <span className="section-icon">🎨</span>
            Aparência
          </h2>
          
          <div className="setting-group">
            <div className="toggle-setting">
              <div className="toggle-info">
                <span className="toggle-label">🌙 Modo Escuro</span>
                <span className="toggle-description">Ativar tema escuro</span>
              </div>
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={isDarkMode}
                  onChange={async (e) => {
                    const newMode = e.target.checked;
                    setIsDarkMode(newMode);
                    // Um só mecanismo de tema (utils/theme.js): escreve `:root.dark`, `data-theme` e
                    // `body.dark-mode` com a chave única `theme`. Antes escrevia `isDarkMode` + data-theme
                    // (sem body.dark-mode) e chamava a API com um campo que o servidor não tem.
                    applyTheme(newMode);
                    toast.success('Aparência guardada neste dispositivo');
                  }}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </section>

        {/* Company Settings Section */}
        {company && (
          <section className="settings-section">
            <h2>
              <span className="section-icon">🏢</span>
              Dados da Empresa
            </h2>
            
            <div className="setting-group">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Nome da Empresa *</label>
                  <input type="text" value={company.name || ''} onChange={e => setCompany({ ...company, name: e.target.value })}
                    placeholder="Nome do Ginásio"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>NIF *</label>
                  <input type="text" value={company.taxId || ''} onChange={e => setCompany({ ...company, taxId: e.target.value })}
                    placeholder="123456789"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Email</label>
                  <input type="email" value={company.email || ''} onChange={e => setCompany({ ...company, email: e.target.value })}
                    placeholder="contacto@ginasio.pt"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Telefone</label>
                  <input type="text" value={company.phone || ''} onChange={e => setCompany({ ...company, phone: e.target.value })}
                    placeholder="+351 123 456 789"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Morada</label>
                <input type="text" value={company.address || ''} onChange={e => setCompany({ ...company, address: e.target.value })}
                  placeholder="Rua Principal, 123"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Código Postal</label>
                  <input type="text" value={company.postalCode || ''} onChange={e => setCompany({ ...company, postalCode: e.target.value })}
                    placeholder="1234-567"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Cidade</label>
                  <input type="text" value={company.city || ''} onChange={e => setCompany({ ...company, city: e.target.value })}
                    placeholder="Lisboa"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>País</label>
                  <input type="text" value={company.country || ''} onChange={e => setCompany({ ...company, country: e.target.value })}
                    placeholder="Portugal"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Website</label>
                <input type="url" value={company.website || ''} onChange={e => setCompany({ ...company, website: e.target.value })}
                  placeholder="https://www.ginasio.pt"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Logo da Empresa</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input type="url" value={company.logoUrl || ''} onChange={e => setCompany({ ...company, logoUrl: e.target.value })}
                    placeholder="https://exemplo.com/logo.png ou cole URL"
                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                  <button type="button" onClick={() => logoInputRef.current?.click()} 
                    disabled={uploadingLogo}
                    style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #ddd', background: '#f0f0f0', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {uploadingLogo ? '⏳ A enviar...' : '📁 Carregar'}
                  </button>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                </div>
                {company.logoUrl && (
                  <div style={{ marginTop: '8px', padding: '12px', background: '#f9f9f9', borderRadius: '8px', display: 'inline-block' }}>
                    <img src={company.logoUrl} alt="Logo" style={{ maxHeight: '80px', maxWidth: '200px', borderRadius: '4px' }} 
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>

              <button className="btn-primary" onClick={handleSaveCompany} disabled={saving}>
                {saving ? 'A guardar...' : '💾 Guardar Dados da Empresa'}
              </button>
            </div>
          </section>
        )}

        {/* Image Search Provider (Articles) */}
        {company && (
          <section className="settings-section">
            <h2>
              <span className="section-icon">🖼️</span>
              Pesquisa de Imagens (Artigos)
            </h2>

            <div className="setting-group">
              <p className="setting-description">
                Para obter resultados como o Google Imagens (ex: fotos oficiais da Prozis), é necessário configurar um provider.
                <br />
                Para Gerar API Key (Serper / Google Images API) aceda a :{' '}
                <a href="https://serper.dev/" target="_blank" rel="noreferrer">https://serper.dev/</a>
              </p>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Provider</label>
                  <select
                    value={company.imageSearchProvider || ''}
                    onChange={e => setCompany({ ...company, imageSearchProvider: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                  >
                    <option value="">(Desativado)</option>
                    <option value="Serper">Serper (Google Images API)</option>
                  </select>
                  <small style={{ fontSize: '11px', color: '#666' }}>Recomendado para resultados mais realistas.</small>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>API Key</label>
                  <input
                    type="password"
                    value={company.imageSearchApiKey || ''}
                    onChange={e => setCompany({ ...company, imageSearchApiKey: e.target.value })}
                    placeholder="••••••••"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                  />
                  <small style={{ fontSize: '11px', color: '#666' }}>Se já estiver guardada, aparece mascarada.</small>
                </div>
              </div>

              <button className="btn-primary" onClick={handleSaveCompany} disabled={saving}>
                {saving ? 'A guardar...' : '💾 Guardar'}
              </button>
            </div>
          </section>
        )}

        {/* Site Configuration */}
        {company && (
          <section className="settings-section">
            <h2>
              <span className="section-icon">⚙️</span>
              Configurações do Site
            </h2>
            
            <div className="setting-group">
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Título do Site</label>
                <input type="text" value={company.siteTitle || company.name || ''} onChange={e => setCompany({ ...company, siteTitle: e.target.value })}
                  placeholder="Nome do Ginásio"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                <small style={{ fontSize: '11px', color: '#666' }}>Aparece no título do browser e resultados de pesquisa</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Descrição</label>
                <textarea value={company.siteDescription || ''} onChange={e => setCompany({ ...company, siteDescription: e.target.value })}
                  placeholder="Descrição breve do ginásio..."
                  rows="3"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', resize: 'vertical' }} />
                <small style={{ fontSize: '11px', color: '#666' }}>Usado em SEO e partilhas nas redes sociais</small>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Favicon / Ícone do Site</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input type="url" value={company.faviconUrl || ''} onChange={e => setCompany({ ...company, faviconUrl: e.target.value })}
                    placeholder="https://exemplo.com/favicon.ico ou .png"
                    style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                  <button type="button" onClick={() => faviconInputRef.current?.click()} 
                    disabled={uploadingFavicon}
                    style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #ddd', background: '#f0f0f0', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {uploadingFavicon ? '⏳ A enviar...' : '📁 Carregar'}
                  </button>
                  <input ref={faviconInputRef} type="file" accept="image/*,.ico" onChange={handleFaviconUpload} style={{ display: 'none' }} />
                </div>
                <small style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '8px' }}>Aparece no separador do browser. Recomendado: 32x32px ou 16x16px</small>
                {company.faviconUrl && (
                  <div style={{ marginTop: '8px', padding: '8px', background: '#f9f9f9', borderRadius: '8px', display: 'inline-block' }}>
                    <img src={company.faviconUrl} alt="Favicon" style={{ width: '32px', height: '32px' }} 
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Cor Primária</label>
                  <input type="color" value={company.primaryColor || '#3498db'} onChange={e => setCompany({ ...company, primaryColor: e.target.value })}
                    style={{ width: '100%', height: '42px', padding: '4px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Cor Secundária</label>
                  <input type="color" value={company.secondaryColor || '#2ecc71'} onChange={e => setCompany({ ...company, secondaryColor: e.target.value })}
                    style={{ width: '100%', height: '42px', padding: '4px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              </div>

              <button className="btn-primary" onClick={handleSaveCompany} disabled={saving}>
                {saving ? 'A guardar...' : '💾 Guardar Configurações do Site'}
              </button>
            </div>
          </section>
        )}

        {/* Privacy & Terms */}
        <section className="settings-section">
          <h2>
            <span className="section-icon">🔒</span>
            Privacidade & Termos
          </h2>
          
          <div className="setting-group">
            <p className="setting-description">
              Configure links para políticas de privacidade e termos de serviço.
            </p>
            
            {company && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>URL Política de Privacidade</label>
                  <input type="url" value={company.privacyPolicyUrl || ''} onChange={e => setCompany({ ...company, privacyPolicyUrl: e.target.value })}
                    placeholder="https://www.ginasio.pt/privacidade"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>URL Termos de Serviço</label>
                  <input type="url" value={company.termsOfServiceUrl || ''} onChange={e => setCompany({ ...company, termsOfServiceUrl: e.target.value })}
                    placeholder="https://www.ginasio.pt/termos"
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>

                <button className="btn-primary" onClick={handleSaveCompany} disabled={saving}>
                  {saving ? 'A guardar...' : '💾 Guardar'}
                </button>
              </>
            )}
          </div>
        </section>

        {/* Notifications Section */}
        <section className="settings-section">
          <h2>
            <span className="section-icon">🔔</span>
            Notificações
          </h2>
          
          <div className="setting-group">
            <p className="setting-description">
              Configure as notificações do sistema através dos separadores Email e SMS.
            </p>
            <div style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
              <button className="btn-secondary" onClick={() => setActiveTab('email')}>
                📧 Configurar Email
              </button>
              <button className="btn-secondary" onClick={() => setActiveTab('sms')}>
                📱 Configurar SMS
              </button>
            </div>
          </div>
        </section>

        {/* Account Section */}
        <section className="settings-section">
          <h2>
            <span className="section-icon">👤</span>
            {t('settings.account', 'Conta')}
          </h2>
          
          <div className="setting-group">
            <ChangePasswordToggle label={t('profile.changePassword', 'Alterar Palavra-passe')} />
            
            <button className="btn-danger-outline">
              ⚠️ {t('settings.deleteAccount', 'Eliminar Conta')}
            </button>
          </div>
        </section>

        {/* About Section */}
        <section className="settings-section about-section">
          <h2>
            <span className="section-icon">ℹ️</span>
            Sobre
          </h2>
          
          <div className="about-info">
            <p><strong>GyMag</strong></p>
            <p>Versão {APP_VERSION}</p>
            <p className="copyright">© {new Date().getFullYear()} GyMag. Todos os direitos reservados.</p>
          </div>
        </section>
          </>
        )}

        {/* Tab: Email */}
        {activeTab === 'email' && (
          <>
        {/* Email/SMTP Section */}
        {company && (
          <section className="settings-section">
            <h2>
              <span className="section-icon">📧</span>
              Configurações de Email (SMTP)
            </h2>
            
            <div className="setting-group">
              <p className="setting-description">
                Configure o servidor SMTP para enviar emails pela aplicação.
              </p>

              <div className="toggle-setting" style={{ marginBottom: '20px' }}>
                <div className="toggle-info">
                  <span className="toggle-label">✅ Email Ativo</span>
                  <span className="toggle-description">Ativar envio de emails</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={company.smtpEnabled || false}
                    onChange={e => setCompany({ ...company, smtpEnabled: e.target.checked })} />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Servidor SMTP</label>
                  <input type="text" value={company.smtpHost || ''} onChange={e => setCompany({ ...company, smtpHost: e.target.value })}
                    placeholder="smtp.gmail.com" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Porta</label>
                  <input type="number" value={company.smtpPort || 587} onChange={e => setCompany({ ...company, smtpPort: parseInt(e.target.value) })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Utilizador</label>
                  <input type="text" value={company.smtpUser || ''} onChange={e => setCompany({ ...company, smtpUser: e.target.value })}
                    placeholder="email@empresa.pt" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Password</label>
                  <input type="password" value={company.smtpPassword || ''} onChange={e => setCompany({ ...company, smtpPassword: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'end', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Email de Teste</label>
                  <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)}
                    placeholder="seu@email.com" style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
                <button className="btn-secondary" onClick={handleTestEmail} disabled={testing}>
                  {testing ? '⏳ A enviar...' : '📤 Testar'}
                </button>
              </div>

              <button className="btn-primary" onClick={handleSaveCompany} disabled={saving}>
                {saving ? 'A guardar...' : '💾 Guardar Configurações Email'}
              </button>
            </div>
          </section>
        )}

        {/* Email Templates Section */}
        <TemplateManagerSection
          type="email"
          templates={emailTemplates}
          onNew={() => openTemplateModal('email')}
          onEdit={(template) => openTemplateModal('email', template)}
          onDelete={(id) => handleDeleteTemplate('email', id)}
          icon="📧"
          title="Templates de Email"
        />
          </>
        )}

        {/* Tab: SMS */}
        {activeTab === 'sms' && (
          <>
        {/* SMS Configuration Section */}
        {company && (
          <section className="settings-section">
            <h2>
              <span className="section-icon">📱</span>
              Configurações de SMS
            </h2>
            
            <div className="setting-group">
              <p className="setting-description">
                As mensagens saem pelo MagSend, configurado no servidor. Aqui liga ou desliga o envio.
              </p>

              <div className="toggle-setting" style={{ marginBottom: '20px' }}>
                <div className="toggle-info">
                  <span className="toggle-label">✅ SMS Ativo</span>
                  <span className="toggle-description">Ativar envio de SMS</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={company.smsEnabled || false}
                    onChange={e => setCompany({ ...company, smsEnabled: e.target.checked })} />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>Nome do remetente</label>
                <input value={company.smsSenderId || ''} placeholder="GyMag"
                  onChange={e => setCompany({ ...company, smsSenderId: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
                <span style={{ fontSize: '11px', opacity: 0.7 }}>É o nome que fica no histórico de envios.</span>
              </div>

              <button className="btn-primary" onClick={handleSaveCompany} disabled={saving}>
                {saving ? 'A guardar...' : '💾 Guardar Configurações SMS'}
              </button>
            </div>
          </section>
        )}

        {/* SMS Templates Section */}
        <TemplateManagerSection
          type="sms"
          templates={smsTemplates}
          onNew={() => openTemplateModal('sms')}
          onEdit={(template) => openTemplateModal('sms', template)}
          onDelete={(id) => handleDeleteTemplate('sms', id)}
          icon="💬"
          title="Templates de SMS"
        />

        {/* SMS Parameters Section */}
        {company && company.smsEnabled && (
          <section className="settings-section">
            <h2>
              <span className="section-icon">⚙️</span>
              Parâmetros de Envio SMS
            </h2>
            
            <div className="setting-group">
              <p className="setting-description">
                Configure quando as mensagens SMS devem ser enviadas automaticamente.
              </p>

              <div className="toggle-setting">
                <div className="toggle-info">
                  <span className="toggle-label">📄 Enviar SMS Documentos</span>
                  <span className="toggle-description">Notifica quando faturas/recibos são emitidos</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={company.smsParamSendDocuments || false}
                    onChange={e => setCompany({ ...company, smsParamSendDocuments: e.target.checked })} />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="toggle-setting">
                <div className="toggle-info">
                  <span className="toggle-label">📢 Envio Notificação (Geral)</span>
                  <span className="toggle-description">Envia SMS quando há novas notificações</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={company.smsParamSendNotifications || false}
                    onChange={e => setCompany({ ...company, smsParamSendNotifications: e.target.checked })} />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              <div className="toggle-setting">
                <div className="toggle-info">
                  <span className="toggle-label">🏋️ Notificação Marcação Treino</span>
                  <span className="toggle-description">Lembrete antes do treino marcado</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" checked={company.smsParamSendBooking || false}
                    onChange={e => setCompany({ ...company, smsParamSendBooking: e.target.checked })} />
                  <span className="toggle-slider"></span>
                </label>
              </div>

              {company.smsParamSendBooking && (
                <div style={{ marginLeft: '40px', marginTop: '12px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px' }}>⏰ Antecedência (minutos)</label>
                  <input type="number" value={company.smsParamBookingAdvanceMinutes || 30}
                    onChange={e => setCompany({ ...company, smsParamBookingAdvanceMinutes: parseInt(e.target.value) || 30 })}
                    min="0" step="5" style={{ width: '150px', padding: '8px', borderRadius: '8px', border: '1px solid #ddd' }} />
                </div>
              )}

              <button className="btn-primary" onClick={handleSaveCompany} disabled={saving} style={{ marginTop: '20px' }}>
                {saving ? 'A guardar...' : '💾 Guardar Parâmetros SMS'}
              </button>
            </div>
          </section>
        )}

        </>
      )}

        {/* Tab: Documentos */}
        {activeTab === 'documentos' && (
          <>

        {/* Defaults per Document Type */}
        <section className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2>
              <span className="section-icon">🧾</span>
              Valores Padrão por Tipo de Documento
            </h2>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn-secondary" onClick={() => { setSeriesForm({ id: null, documentType: 'FT', name: '', code: '', isDefault: false }); setShowSeriesModal(true); }}>
                + Série
              </button>
              <button className="btn-secondary" onClick={() => { setSellerForm({ id: null, name: '', code: '', isDefault: false }); setShowSellerModal(true); }}>
                + Vendedor
              </button>
              <button className="btn-secondary" onClick={() => { setDocDefaultsForm({ documentType: 'FT', defaultLayoutId: '', defaultSeriesId: '', defaultSellerId: '' }); setShowDocDefaultsModal(true); }}>
                + Configuração
              </button>
            </div>
          </div>

          <div className="setting-group">
            <p className="setting-description">
              Apenas pode existir <strong>uma</strong> configuração por tipo de documento. Aqui define o <strong>layout de impressão</strong>, a <strong>série</strong> e o <strong>vendedor</strong> por defeito.
            </p>

            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: `1px solid ${isDarkMode ? '#34495e' : '#e5e7eb'}` }}>
                    <th style={{ padding: '10px 8px', fontSize: '12px', opacity: 0.85 }}>Documento</th>
                    <th style={{ padding: '10px 8px', fontSize: '12px', opacity: 0.85 }}>Layout</th>
                    <th style={{ padding: '10px 8px', fontSize: '12px', opacity: 0.85 }}>Série</th>
                    <th style={{ padding: '10px 8px', fontSize: '12px', opacity: 0.85 }}>Vendedor</th>
                    <th style={{ padding: '10px 8px', fontSize: '12px', opacity: 0.85, width: '120px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {DOC_TYPES.map(dt => {
                    const row = (documentDefaults || []).find(x => (x.documentType || '').toUpperCase() === dt.value);
                    return (
                      <tr key={dt.value} style={{ borderBottom: `1px solid ${isDarkMode ? '#34495e' : '#f1f5f9'}` }}>
                        <td style={{ padding: '10px 8px', fontSize: '13px', fontWeight: 600 }}>{dt.label}</td>
                        <td style={{ padding: '10px 8px', fontSize: '13px' }}>{row?.defaultLayoutName || '—'}</td>
                        <td style={{ padding: '10px 8px', fontSize: '13px' }}>{row?.defaultSeriesCode || '—'}</td>
                        <td style={{ padding: '10px 8px', fontSize: '13px' }}>{row?.defaultSellerName || '—'}</td>
                        <td style={{ padding: '10px 8px' }}>
                          <button
                            className="btn-secondary"
                            style={{ fontSize: '12px', padding: '6px 10px' }}
                            onClick={() => {
                              setDocDefaultsForm({
                                documentType: dt.value,
                                defaultLayoutId: row?.defaultLayoutId ? String(row.defaultLayoutId) : '',
                                defaultSeriesId: row?.defaultSeriesId ? String(row.defaultSeriesId) : '',
                                defaultSellerId: row?.defaultSellerId ? String(row.defaultSellerId) : '',
                              });
                              setShowDocDefaultsModal(true);
                            }}
                          >
                            ✏️ Editar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ marginTop: '12px', fontSize: '12px', opacity: 0.8 }}>
              Séries configuradas: {(documentSeries || []).length} • Vendedores: {(documentSellers || []).length}
            </div>
          </div>
        </section>

        {/* Document Layouts Section */}
        <section className="settings-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2>
              <span className="section-icon">📐</span>
              Layouts de Documentos
            </h2>
            <button 
              className="btn-secondary"
              onClick={() => {
                setLayoutForm({
                  name: '',
                  documentType: 'FT',
                  pageSize: 'A4',
                  orientation: 'Portrait',
                  marginTop: 20,
                  marginBottom: 20,
                  marginLeft: 20,
                  marginRight: 20,
                  logoUrl: '',
                  primaryColor: '#3498db',
                  fontSize: 12,
                  isDefault: false
                });
                setShowLayoutDesigner(true);
              }}
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              + Novo Layout
            </button>
          </div>
          
          <div className="setting-group">
            <p className="setting-description">
              Configure os layouts de impressão para faturas, recibos e outros documentos.
            </p>

            {/* Layouts Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px', marginTop: '16px' }}>
              {layouts.map(layout => (
                <div key={layout.id} style={{ background: isDarkMode ? '#2c3e50' : '#fff', border: `1px solid ${isDarkMode ? '#34495e' : '#e0e0e0'}`, borderRadius: '12px', padding: '16px', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                  {layout.isDefault && (
                    <div style={{ position: 'absolute', top: '10px', right: '10px', background: '#27ae60', color: '#fff', padding: '4px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600' }}>
                      PADRÃO
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>
                      {layout.name || layout.documentType}
                    </div>
                    <div style={{ fontSize: '12px', opacity: 0.7 }}>
                      {layout.documentType}
                    </div>
                  </div>

                  <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                    <div>📄 Formato: {layout.pageSize || 'A4'}</div>
                    <div>📐 Orientação: {layout.orientation || 'Portrait'}</div>
                    {layout.logoUrl && <div>🖼️ Com logotipo</div>}
                  </div>

                  <div style={{ borderTop: `1px solid ${isDarkMode ? '#34495e' : '#e0e0e0'}`, paddingTop: '12px', marginTop: '12px', display: 'flex', gap: '8px' }}>
                    <button 
                      className="btn-secondary"
                      onClick={() => {
                        setLayoutForm(layout);
                        setShowLayoutDesigner(true);
                      }}
                      style={{ flex: 1, fontSize: '12px', padding: '8px' }}
                    >
                      ✏️ Editar
                    </button>
                    <button 
                      className="btn-danger-outline"
                      onClick={async () => {
                        if (window.confirm('Tem certeza que deseja eliminar este layout?')) {
                          try {
                            await api.delete(`/billing/layouts/${layout.id}`);
                            toast.success('Layout eliminado');
                            loadSettings();
                          } catch (error) {
                            toast.error('Erro ao eliminar layout');
                          }
                        }
                      }}
                      style={{ fontSize: '12px', padding: '8px 12px' }}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              ))}

              {/* Empty State */}
              {layouts.length === 0 && (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 20px', background: isDarkMode ? '#2c3e50' : '#f8f9fa', borderRadius: '12px', border: `2px dashed ${isDarkMode ? '#34495e' : '#ddd'}` }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📐</div>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Nenhum layout criado</div>
                  <div style={{ fontSize: '13px', opacity: 0.7, marginBottom: '20px' }}>Crie o seu primeiro layout de documento para personalizar a impressão</div>
                  <button 
                    className="btn-secondary"
                    onClick={() => {
                      setLayoutForm({
                        name: '',
                        documentType: 'FT',
                        pageSize: 'A4',
                        orientation: 'Portrait',
                        marginTop: 20,
                        marginBottom: 20,
                        marginLeft: 20,
                        marginRight: 20,
                        logoUrl: '',
                        primaryColor: '#3498db',
                        fontSize: 12,
                        isDefault: false
                      });
                      setShowLayoutDesigner(true);
                    }}
                  >
                    + Criar Primeiro Layout
                  </button>
                </div>
              )}
            </div>

            {/* Info Card */}
            <div style={{ marginTop: '24px', background: isDarkMode ? '#1e3a5f' : '#e3f2fd', border: `1px solid ${isDarkMode ? '#2980b9' : '#64b5f6'}`, borderRadius: '8px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '12px' }}>
                <span style={{ fontSize: '24px' }}>💡</span>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: isDarkMode ? '#64b5f6' : '#1976d2', marginBottom: '6px' }}>Sobre Layouts</div>
                  <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.9 }}>
                    Os layouts permitem personalizar a aparência dos seus documentos impressos. Pode definir logotipos, cores, fontes, 
                    posicionamento de campos e muito mais. Cada tipo de documento (Fatura, Recibo, etc.) pode ter o seu layout específico.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Layout Designer Modal */}
      {showLayoutDesigner && layoutForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ background: isDarkMode ? '#2c3e50' : '#fff', borderRadius: '16px', width: '95%', maxWidth: '1200px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${isDarkMode ? '#34495e' : '#e0e0e0'}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>📐 {layoutForm.id ? 'Editar Layout' : 'Novo Layout'}</h2>
                <p style={{ fontSize: '13px', opacity: 0.7, margin: '4px 0 0 0' }}>Configure a aparência dos documentos impressos</p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => { setShowLayoutDesigner(false); setShowLayoutDesignerPro(true); }}
                  style={{ background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }}
                  title="Abrir designer avançado (estilo List & Label)"
                >
                  ✨ Designer Pro
                </button>
                <button onClick={() => setShowLayoutDesigner(false)} style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>✕ Fechar</button>
              </div>
            </div>

            {/* Content */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', padding: '24px', overflow: 'auto', flex: 1 }}>
              {/* Settings Panel */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>⚙️ Configurações</h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', opacity: 0.8 }}>NOME DO LAYOUT</label>
                    <input 
                      type="text" 
                      value={layoutForm.name} 
                      onChange={e => setLayoutForm({...layoutForm, name: e.target.value})}
                      placeholder="Ex: Fatura Padrão"
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '14px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', opacity: 0.8 }}>TIPO DE DOCUMENTO</label>
                    <select 
                      value={layoutForm.documentType} 
                      onChange={e => setLayoutForm({...layoutForm, documentType: e.target.value})}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '14px' }}
                    >
                      <option value="FT">Fatura</option>
                      <option value="FR">Fatura Recibo</option>
                      <option value="FS">Fatura Simplificada</option>
                      <option value="RC">Recibo</option>
                      <option value="NC">Nota de Crédito</option>
                      <option value="ND">Nota de Débito</option>
                      <option value="OR">Encomenda Cliente</option>
                      <option value="ORC">Orçamento</option>
                      <option value="PP">Fatura Pro-Forma</option>
                      <option value="GT">Guia de Transporte</option>
                      <option value="FC">Fatura de Compra</option>
                      <option value="GR">Guia de Receção (Compras)</option>
                      <option value="MS">Movimentos de Stock</option>
                      <option value="INV">Inventário</option>
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', opacity: 0.8 }}>FORMATO</label>
                      <select 
                        value={layoutForm.paperSize || layoutForm.pageSize || 'A4'} 
                        onChange={e => setLayoutForm({ ...layoutForm, paperSize: e.target.value, pageSize: e.target.value })}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '14px' }}
                      >
                        <option value="A4">A4</option>
                        <option value="A5">A5</option>
                        <option value="Letter">Letter</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', opacity: 0.8 }}>ORIENTAÇÃO</label>
                      <select 
                        value={layoutForm.orientation} 
                        onChange={e => setLayoutForm({...layoutForm, orientation: e.target.value})}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '14px' }}
                      >
                        <option value="Portrait">Vertical</option>
                        <option value="Landscape">Horizontal</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '8px', opacity: 0.8 }}>MARGENS (mm)</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                      <input type="number" placeholder="Topo" value={layoutForm.marginTop} onChange={e => setLayoutForm({...layoutForm, marginTop: e.target.value})} style={{ padding: '8px', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '13px' }} />
                      <input type="number" placeholder="Fundo" value={layoutForm.marginBottom} onChange={e => setLayoutForm({...layoutForm, marginBottom: e.target.value})} style={{ padding: '8px', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '13px' }} />
                      <input type="number" placeholder="Esquerda" value={layoutForm.marginLeft} onChange={e => setLayoutForm({...layoutForm, marginLeft: e.target.value})} style={{ padding: '8px', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '13px' }} />
                      <input type="number" placeholder="Direita" value={layoutForm.marginRight} onChange={e => setLayoutForm({...layoutForm, marginRight: e.target.value})} style={{ padding: '8px', borderRadius: '6px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '13px' }} />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', opacity: 0.8 }}>URL DO LOGOTIPO</label>
                    <input 
                      type="text" 
                      value={layoutForm.companyLogoUrl || layoutForm.logoUrl || ''} 
                      onChange={e => setLayoutForm({ ...layoutForm, companyLogoUrl: e.target.value, logoUrl: e.target.value })}
                      placeholder="https://..."
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '14px' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', opacity: 0.8 }}>COR PRINCIPAL</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input 
                          type="color" 
                          value={layoutForm.primaryColor} 
                          onChange={e => setLayoutForm({...layoutForm, primaryColor: e.target.value})}
                          style={{ width: '50px', height: '42px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
                        />
                        <input 
                          type="text" 
                          value={layoutForm.primaryColor} 
                          onChange={e => setLayoutForm({...layoutForm, primaryColor: e.target.value})}
                          style={{ flex: 1, padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '14px' }}
                        />
                      </div>
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px', opacity: 0.8 }}>TAMANHO DA FONTE</label>
                      <input 
                        type="number" 
                        min="8" 
                        max="16" 
                        value={layoutForm.fontSize} 
                        onChange={e => setLayoutForm({...layoutForm, fontSize: e.target.value})}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#34495e' : '#ddd'}`, background: isDarkMode ? '#34495e' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '14px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: isDarkMode ? '#34495e' : '#f8f9fa', borderRadius: '8px' }}>
                    <input 
                      type="checkbox" 
                      id="isDefault"
                      checked={layoutForm.isDefault} 
                      onChange={e => setLayoutForm({...layoutForm, isDefault: e.target.checked})}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="isDefault" style={{ fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Definir como layout padrão</label>
                  </div>

                  {/* Advanced HTML/CSS mode */}
                  <div style={{ padding: '12px', background: isDarkMode ? '#34495e' : '#f8f9fa', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#3d566e' : '#e5e7eb'}` }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '6px' }}>🧩 Modo Avançado (HTML/CSS)</div>
                    <div style={{ fontSize: '12px', opacity: 0.85, marginBottom: '10px', lineHeight: 1.4 }}>
                      Se preencher o <strong>Body HTML</strong>, a impressão (HTML) usa este template.
                      <br />
                      Tags úteis: <span style={{ fontFamily: 'monospace' }}>{'{{CompanyName}}'} {'{{ClientName}}'} {'{{LinesTable}}'} {'{{Total}}'} {'{{FooterText}}'}</span>
                    </div>

                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '6px', opacity: 0.9 }}>BODY HTML</label>
                    <textarea
                      value={layoutForm.bodyHtml || ''}
                      onChange={e => setLayoutForm({ ...layoutForm, bodyHtml: e.target.value })}
                      placeholder={`Exemplo:\n<div>\n  <h2 style=\"color: {{PrimaryColor}}\">{{DocumentType}} {{InvoiceNumberOnly}}</h2>\n  <div>{{CompanyName}} • NIF {{CompanyNIF}}</div>\n  <hr/>\n  {{LinesTable}}\n  <div style=\"text-align:right\"><strong>Total:</strong> {{Total}}</div>\n  <div style=\"margin-top:16px\">{{FooterText}}</div>\n</div>`}
                      rows={8}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#3d566e' : '#ddd'}`, background: isDarkMode ? '#2c3e50' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '12px', fontFamily: 'monospace', resize: 'vertical' }}
                    />

                    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', margin: '10px 0 6px', opacity: 0.9 }}>CUSTOM CSS</label>
                    <textarea
                      value={layoutForm.customCss || ''}
                      onChange={e => setLayoutForm({ ...layoutForm, customCss: e.target.value })}
                      placeholder={`/* CSS extra para impressão */\n.table td { padding: 6px; }`}
                      rows={5}
                      style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${isDarkMode ? '#3d566e' : '#ddd'}`, background: isDarkMode ? '#2c3e50' : '#fff', color: isDarkMode ? '#fff' : '#000', fontSize: '12px', fontFamily: 'monospace', resize: 'vertical' }}
                    />
                  </div>

                  <button 
                    onClick={async () => {
                      try {
                        const payload = {
                          ...layoutForm,
                          paperSize: layoutForm.paperSize || layoutForm.pageSize || 'A4',
                          companyLogoUrl: layoutForm.companyLogoUrl || layoutForm.logoUrl || '',
                          fontSize: Number(layoutForm.fontSize || 12)
                        };
                        if (layoutForm.id) {
                          await api.put(`/billing/layouts/${layoutForm.id}`, payload);
                          toast.success('Layout atualizado!');
                        } else {
                          await api.post('/billing/layouts', payload);
                          toast.success('Layout criado!');
                        }
                        setShowLayoutDesigner(false);
                        loadSettings();
                      } catch (error) {
                        toast.error('Erro ao guardar layout');
                      }
                    }}
                    style={{ width: '100%', padding: '12px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', marginTop: '8px' }}
                  >
                    💾 Guardar Layout
                  </button>
                </div>
              </div>

              {/* Preview Panel */}
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>👁️ Pré-visualização</h3>
                <div style={{ background: '#f0f0f0', padding: '20px', borderRadius: '12px', aspectRatio: layoutForm.orientation === 'Portrait' ? '210/297' : '297/210' }}>
                  <div style={{ background: '#fff', height: '100%', borderRadius: '4px', padding: `${layoutForm.marginTop}px ${layoutForm.marginRight}px ${layoutForm.marginBottom}px ${layoutForm.marginLeft}px`, fontSize: `${layoutForm.fontSize}px`, overflow: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    {layoutForm.bodyHtml ? (
                      <div dangerouslySetInnerHTML={{ __html: buildAdvancedLayoutPreviewHtml(layoutForm) }} />
                    ) : (
                      <>
                        {/* Header */}
                        <div style={{ borderBottom: `3px solid ${layoutForm.primaryColor}`, paddingBottom: '12px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {layoutForm.logoUrl && (
                            <img src={layoutForm.logoUrl} alt="Logo" style={{ height: '40px', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
                          )}
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '1.5em', fontWeight: '700', color: layoutForm.primaryColor }}>{layoutForm.documentType} #001</div>
                            <div style={{ fontSize: '0.9em', color: '#666' }}>Data: {new Date().toLocaleDateString()}</div>
                          </div>
                        </div>

                        {/* Company & Client Info */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px', fontSize: '0.85em' }}>
                          <div>
                            <div style={{ fontWeight: '700', marginBottom: '6px', color: layoutForm.primaryColor }}>Empresa</div>
                            <div>Nome da Empresa Lda.</div>
                            <div>NIF: 123456789</div>
                            <div>Morada, Cidade</div>
                          </div>
                          <div>
                            <div style={{ fontWeight: '700', marginBottom: '6px', color: layoutForm.primaryColor }}>Cliente</div>
                            <div>Nome do Cliente</div>
                            <div>NIF: 987654321</div>
                            <div>Morada, Cidade</div>
                          </div>
                        </div>

                        {/* Items Table */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px', fontSize: '0.85em' }}>
                          <thead>
                            <tr style={{ background: layoutForm.primaryColor, color: '#fff' }}>
                              <th style={{ padding: '8px', textAlign: 'left' }}>Descrição</th>
                              <th style={{ padding: '8px', textAlign: 'right' }}>Qtd</th>
                              <th style={{ padding: '8px', textAlign: 'right' }}>Preço</th>
                              <th style={{ padding: '8px', textAlign: 'right' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr style={{ borderBottom: '1px solid #eee' }}>
                              <td style={{ padding: '8px' }}>Produto/Serviço Exemplo</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>1</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>100,00 €</td>
                              <td style={{ padding: '8px', textAlign: 'right' }}>100,00 €</td>
                            </tr>
                          </tbody>
                        </table>

                        {/* Totals */}
                        <div style={{ textAlign: 'right', fontSize: '0.9em' }}>
                          <div style={{ marginBottom: '4px' }}>Subtotal: 100,00 €</div>
                          <div style={{ marginBottom: '8px' }}>IVA (23%): 23,00 €</div>
                          <div style={{ fontSize: '1.3em', fontWeight: '700', color: layoutForm.primaryColor }}>Total: 123,00 €</div>
                        </div>

                        {/* Footer */}
                        <div style={{ marginTop: '24px', paddingTop: '12px', borderTop: '1px solid #eee', fontSize: '0.75em', color: '#666', textAlign: 'center' }}>
                          Documento processado por computador • {new Date().toLocaleString()}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showLayoutDesignerPro && layoutForm && (
        <LayoutDesignerPro
          isDarkMode={isDarkMode}
          layout={layoutForm}
          onClose={() => setShowLayoutDesignerPro(false)}
          onApply={(updated) => {
            setLayoutForm(updated);
            setShowLayoutDesignerPro(false);
            setShowLayoutDesigner(true);
          }}
        />
      )}

      {/* Document Defaults Modal */}
      {showDocDefaultsModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2002 }}>
          <div style={{ background: isDarkMode ? '#2c3e50' : '#fff', borderRadius: '16px', width: '95%', maxWidth: '760px', padding: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>🧾 Valores Padrão do Documento</h2>
              <button className="btn-danger-outline" onClick={() => setShowDocDefaultsModal(false)}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Documento</label>
                <select value={docDefaultsForm.documentType} onChange={e => setDocDefaultsForm({ ...docDefaultsForm, documentType: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}>
                  {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label} ({d.value})</option>)}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Layout de Impressão</label>
                <select value={docDefaultsForm.defaultLayoutId} onChange={e => setDocDefaultsForm({ ...docDefaultsForm, defaultLayoutId: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}>
                  <option value="">(Sem layout por defeito)</option>
                  {(layouts || []).filter(l => l.isActive !== false).map(l => (
                    <option key={l.id} value={l.id}>{l.name} — {l.documentType}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Série</label>
                <select value={docDefaultsForm.defaultSeriesId} onChange={e => setDocDefaultsForm({ ...docDefaultsForm, defaultSeriesId: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}>
                  <option value="">(Sem série)</option>
                  {(documentSeries || []).filter(s => (s.documentType || '').toUpperCase() === (docDefaultsForm.documentType || '').toUpperCase()).map(s => (
                    <option key={s.id} value={s.id}>{s.code} — {s.name}{s.isDefault ? ' (padrão)' : ''}</option>
                  ))}
                </select>
                <div style={{ marginTop: '6px' }}>
                  <button className="btn-secondary" style={{ fontSize: '12px', padding: '6px 10px' }} onClick={() => { setSeriesForm({ id: null, documentType: docDefaultsForm.documentType || 'FT', name: '', code: '', isDefault: false }); setShowSeriesModal(true); }}>
                    + Criar Série
                  </button>
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Vendedor</label>
                <select value={docDefaultsForm.defaultSellerId} onChange={e => setDocDefaultsForm({ ...docDefaultsForm, defaultSellerId: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}>
                  <option value="">(Sem vendedor)</option>
                  {(documentSellers || []).map(s => (
                    <option key={s.id} value={s.id}>{s.name}{s.isDefault ? ' (padrão)' : ''}</option>
                  ))}
                </select>
                <div style={{ marginTop: '6px' }}>
                  <button className="btn-secondary" style={{ fontSize: '12px', padding: '6px 10px' }} onClick={() => { setSellerForm({ id: null, name: '', code: '', isDefault: false }); setShowSellerModal(true); }}>
                    + Criar Vendedor
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button className="btn-secondary" onClick={() => setShowDocDefaultsModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={upsertDocDefaults}>💾 Guardar</button>
            </div>
          </div>
        </div>
      )}

      {/* Series Modal */}
      {showSeriesModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2003 }}>
          <div style={{ background: isDarkMode ? '#2c3e50' : '#fff', borderRadius: '16px', width: '95%', maxWidth: '760px', padding: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>🧷 Séries</h2>
              <button className="btn-danger-outline" onClick={() => setShowSeriesModal(false)}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Tipo Documento</label>
                <select value={seriesForm.documentType} onChange={e => setSeriesForm({ ...seriesForm, documentType: e.target.value })}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}>
                  {DOC_TYPES.filter(d => d.value !== 'RC').map(d => <option key={d.value} value={d.value}>{d.label} ({d.value})</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Código</label>
                <input value={seriesForm.code} onChange={e => setSeriesForm({ ...seriesForm, code: e.target.value })}
                  placeholder="Ex: 8"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Nome</label>
                <input value={seriesForm.name} onChange={e => setSeriesForm({ ...seriesForm, name: e.target.value })}
                  placeholder="Ex: Série 8"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={!!seriesForm.isDefault} onChange={e => setSeriesForm({ ...seriesForm, isDefault: e.target.checked })} />
                <span style={{ fontSize: '13px' }}>Definir como série padrão (para este tipo)</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button className="btn-secondary" onClick={() => setShowSeriesModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={saveSeries}>💾 Guardar</button>
            </div>

            <div style={{ marginTop: '18px', borderTop: `1px solid ${isDarkMode ? '#34495e' : '#e5e7eb'}`, paddingTop: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Séries existentes</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
                {(documentSeries || []).filter(s => s.isActive !== false).map(s => (
                  <div key={s.id} style={{ border: `1px solid ${isDarkMode ? '#34495e' : '#e5e7eb'}`, borderRadius: '10px', padding: '10px' }}>
                    <div style={{ fontWeight: 700 }}>{s.documentType} • {s.code}</div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>{s.name}{s.isDefault ? ' (padrão)' : ''}</div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                      <button className="btn-secondary" style={{ fontSize: '12px', padding: '6px 10px' }} onClick={() => { setSeriesForm({ id: s.id, documentType: s.documentType, name: s.name, code: s.code, isDefault: !!s.isDefault }); }}>
                        ✏️
                      </button>
                      <button className="btn-danger-outline" style={{ fontSize: '12px', padding: '6px 10px' }} onClick={async () => { if (!window.confirm('Desativar esta série?')) return; await api.delete(`/billing/series/${s.id}`); loadSettings(); }}>
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sellers Modal */}
      {showSellerModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2004 }}>
          <div style={{ background: isDarkMode ? '#2c3e50' : '#fff', borderRadius: '16px', width: '95%', maxWidth: '760px', padding: '22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <h2 style={{ margin: 0, fontSize: '18px' }}>🧑‍💼 Vendedores</h2>
              <button className="btn-danger-outline" onClick={() => setShowSellerModal(false)}>✕</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Nome</label>
                <input value={sellerForm.name} onChange={e => setSellerForm({ ...sellerForm, name: e.target.value })}
                  placeholder="Ex: Decorarte"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '6px' }}>Código (opcional)</label>
                <input value={sellerForm.code} onChange={e => setSellerForm({ ...sellerForm, code: e.target.value })}
                  placeholder="Ex: 0"
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }} />
              </div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={!!sellerForm.isDefault} onChange={e => setSellerForm({ ...sellerForm, isDefault: e.target.checked })} />
                <span style={{ fontSize: '13px' }}>Definir como vendedor padrão</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
              <button className="btn-secondary" onClick={() => setShowSellerModal(false)}>Cancelar</button>
              <button className="btn-primary" onClick={saveSeller}>💾 Guardar</button>
            </div>

            <div style={{ marginTop: '18px', borderTop: `1px solid ${isDarkMode ? '#34495e' : '#e5e7eb'}`, paddingTop: '12px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, marginBottom: '8px' }}>Vendedores existentes</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
                {(documentSellers || []).filter(s => s.isActive !== false).map(s => (
                  <div key={s.id} style={{ border: `1px solid ${isDarkMode ? '#34495e' : '#e5e7eb'}`, borderRadius: '10px', padding: '10px' }}>
                    <div style={{ fontWeight: 700 }}>{s.name}{s.code ? ` • ${s.code}` : ''}</div>
                    <div style={{ fontSize: '12px', opacity: 0.8 }}>{s.isDefault ? 'padrão' : ''}</div>
                    <div style={{ marginTop: '8px', display: 'flex', gap: '8px' }}>
                      <button className="btn-secondary" style={{ fontSize: '12px', padding: '6px 10px' }} onClick={() => { setSellerForm({ id: s.id, name: s.name, code: s.code || '', isDefault: !!s.isDefault }); }}>
                        ✏️
                      </button>
                      <button className="btn-danger-outline" style={{ fontSize: '12px', padding: '6px 10px' }} onClick={async () => { if (!window.confirm('Desativar este vendedor?')) return; await api.delete(`/billing/sellers/${s.id}`); loadSettings(); }}>
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

          </>
        )}

        {/* Tab: Integrações (Google Fit, Stripe, etc.) */}
        {activeTab === 'integrations' && (
          <>
            <section className="settings-section">
              <h2>
                <span className="section-icon">🔌</span>
                Integrações
              </h2>
              <p style={{ marginTop: '6px', color: '#6b7280' }}>
                Configure integrações como Google Fit (OAuth), Stripe, Email, etc.
              </p>
            </section>

            <div style={{ marginTop: '12px' }}>
              <AdminConfiguration allowedCategories={['GoogleFit', 'GoogleCalendar', 'Stripe', 'MBWay']} />
            </div>
          </>
        )}
      </div>

      {/* Template Editor Modal */}
      <TemplateModal
        show={showTemplateModal}
        onClose={closeTemplateModal}
        template={editingTemplate}
        onChange={setEditingTemplate}
        onSave={handleSaveTemplate}
        type={templateType}
      />
    </div>
  );
};

export default SettingsPage;
